import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * AddressPinInput — the address field used on both the New Opening form
 * and the Business Profile page.
 *
 * Features:
 *   1. Autocomplete dropdown as the user types (debounced 300ms, min
 *      3 chars). Hits `/api/geocode/suggest` which proxies Photon
 *      (OSM-based, no API key). Clicking a suggestion fills the input
 *      AND pins the coords in one shot — no second geocode round-trip.
 *   2. "Pin on map" button as a fallback for the long-tail case where
 *      the user's address isn't in the suggestions.
 *   3. Confirmation banner (green-left-border) showing the pinned
 *      display_name + lat/lng once the user commits.
 *   4. Editing the input clears the banner so a stale display_name /
 *      coord pair can never survive to the backend.
 *
 * Props:
 *   value          — current address string (controlled)
 *   onChange       — (string) => void
 *   onPinned       — ({lat, lng, display_name}) => void (optional)
 *   onCleared      — () => void (optional; fired when user edits + pin resets)
 *   initialPinned  — {lat, lng, display_name} to pre-populate the banner
 *   required       — HTML required passthrough
 *   placeholder    — input placeholder
 *   testIdPrefix   — e.g. "profile-address"
 */
export default function AddressPinInput({
  value,
  onChange,
  onPinned,
  onCleared,
  initialPinned = null,
  required = false,
  placeholder = "123 Market St, Sydney",
  testIdPrefix = "address",
}) {
  const [coords, setCoords] = useState(initialPinned);
  const [busy, setBusy] = useState(false);
  const [pinnedAddress, setPinnedAddress] = useState(
    initialPinned ? value : null
  );
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // Highlight index for keyboard navigation of the dropdown. -1 = nothing
  // highlighted (Enter will submit the form or trigger the pin button).
  const [activeIdx, setActiveIdx] = useState(-1);
  // Suppress the next suggestion fetch — set by applySuggestion so clicking
  // a dropdown item doesn't immediately re-open the dropdown from the
  // onChange triggered when we fill the input.
  const suppressNextFetchRef = useRef(false);
  const wrapperRef = useRef(null);
  const abortRef = useRef(null);

  // Clear the pin banner the instant the user types a different address.
  useEffect(() => {
    if (pinnedAddress !== null && value !== pinnedAddress) {
      setCoords(null);
      setPinnedAddress(null);
      onCleared?.();
    }
  }, [value, pinnedAddress, onCleared]);

  // Debounced autocomplete fetch. 300ms is the sweet spot — faster feels
  // jittery, slower feels sluggish.
  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }
    const q = (value || "").trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      // Abort any in-flight request so a slow earlier response can't
      // overwrite a newer one.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSuggesting(true);
      try {
        const { data } = await api.get(
          `/geocode/suggest?q=${encodeURIComponent(q)}&limit=5`,
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        setSuggestions(data?.results || []);
        setShowSuggestions(true);
        setActiveIdx(-1);
      } catch (e) {
        // Non-fatal — suggestions are an enhancement, not critical.
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          // eslint-disable-next-line no-console
          console.warn("[suggest] failed", e?.message || e);
        }
      } finally {
        setSuggesting(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [value]);

  // Close the dropdown when clicking outside the wrapper.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const applySuggestion = (s) => {
    suppressNextFetchRef.current = true;
    onChange(s.display_name);
    setCoords(s);
    setPinnedAddress(s.display_name);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveIdx(-1);
    onPinned?.(s);
    toast.success("Location pinned on the map");
  };

  const runGeocode = async () => {
    const addr = value.trim();
    if (addr.length < 3) {
      toast.error("Enter an address to pin");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/geocode", { address: addr });
      setCoords(data);
      setPinnedAddress(value);
      setShowSuggestions(false);
      onPinned?.(data);
      toast.success("Location pinned on the map");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not find that address");
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      applySuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveIdx(-1);
    }
  };

  return (
    <div className="grid gap-2" data-testid={`${testIdPrefix}-section`} ref={wrapperRef}>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative sm:flex-1">
          <Input
            data-testid={`${testIdPrefix}-input`}
            required={required}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="rounded-none h-11 md:h-10 w-full"
            autoComplete="off"
            autoCapitalize="words"
            enterKeyHint="search"
            // role=combobox + aria-* is best-practice for a text input
            // that owns an autosuggest listbox — screen readers announce
            // the suggestion count and current highlight.
            role="combobox"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-autocomplete="list"
            aria-controls={`${testIdPrefix}-listbox`}
            aria-activedescendant={
              activeIdx >= 0 ? `${testIdPrefix}-opt-${activeIdx}` : undefined
            }
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul
              id={`${testIdPrefix}-listbox`}
              role="listbox"
              data-testid={`${testIdPrefix}-suggestions`}
              className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto border border-border bg-background shadow-md"
            >
              {suggestions.map((s, i) => (
                <li
                  key={`${s.lat}-${s.lng}-${i}`}
                  id={`${testIdPrefix}-opt-${i}`}
                  role="option"
                  aria-selected={i === activeIdx}
                  data-testid={`${testIdPrefix}-suggestion-${i}`}
                  onMouseDown={(e) => {
                    // Use mousedown (not click) so the Input's blur
                    // handler can't close the dropdown before we fire.
                    e.preventDefault();
                    applySuggestion(s);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`flex cursor-pointer items-start gap-2 border-b border-border/60 px-3 py-2 text-xs last:border-b-0 ${
                    i === activeIdx ? "bg-muted" : "hover:bg-muted/60"
                  }`}
                >
                  <MapPin
                    size={12}
                    className="mt-0.5 shrink-0 text-[#C9A85F]"
                  />
                  <span className="flex-1 leading-tight">
                    {s.display_name}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {suggesting && !showSuggestions && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Loader2 size={12} className="animate-spin" />
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid={`${testIdPrefix}-pin-btn`}
          onClick={runGeocode}
          disabled={busy || !value?.trim()}
          className="inline-flex h-11 items-center justify-center gap-1.5 border border-foreground px-4 text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors disabled:opacity-50 md:h-10"
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <MapPin size={12} />
          )}
          Pin on map
        </button>
      </div>
      {coords ? (
        <div
          data-testid={`${testIdPrefix}-pin-result`}
          className="mono flex items-start gap-1.5 border-l-2 border-[#34D399] bg-muted/40 p-2 text-[11px] text-muted-foreground"
        >
          <CheckCircle2 size={12} className="mt-0.5 text-[#059669]" />
          <span className="flex-1">
            {coords.display_name}
            <br />
            <span className="text-foreground/70">
              {Number(coords.lat).toFixed(4)}, {Number(coords.lng).toFixed(4)}
            </span>
          </span>
        </div>
      ) : (
        <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Start typing — pick a match from the dropdown, or type the full
          address and click "Pin on map".
        </p>
      )}
    </div>
  );
}
